import { useMemo } from "react";

import { useCustomerId } from "../../../Components/hooks/useCustomerId";
import { useFlexsaveDoc, useFlexsaveRDS, useFlexsaveSageMaker } from "../../Flexsave/hooks";
import { getCurrentMonthId } from "../../Flexsave/utils/dateUtils";
import { calculateSavings, verifyNumber } from "../../SpotScaling/AsgsOverview/Savings/utils";
import { asgsCollection } from "../../SpotScaling/db";
import { useAsgs, useConnectedAccounts } from "../../SpotScaling/hooks";
import {
  type AsgTableItem,
  OptimizationStatus,
  OptimizationStatusColor,
  SavingsTimeFilter,
} from "../../SpotScaling/types";
import { getPotentialSavings } from "../../SpotScaling/Utils/costsUtils";

/**
 * Hook to get spot scaling savings data for current and last month
 */
export function useSpotScalingSavings() {
  const [allAsgs, loading] = useAsgs(asgsCollection);
  const [connectAccounts] = useConnectedAccounts();

  const accountsWithSpotScaling = useMemo(
    () =>
      connectAccounts?.reduce(
        (accumulator, account) => {
          account.supportedFeatures?.forEach((feature) => {
            if (feature.name === "spot-scaling" && feature.hasRequiredPermissions) {
              accumulator[account.accountId] = true;
            }
          });
          return accumulator;
        },
        {} as Record<string, boolean>
      ) ?? {},
    [connectAccounts]
  );

  const asgs = useMemo(
    () => allAsgs?.filter((asg) => accountsWithSpotScaling[asg.accountId] === true),
    [accountsWithSpotScaling, allAsgs]
  );

  const createAsgTableItems = useMemo(
    () =>
      (timeFilter: SavingsTimeFilter): AsgTableItem[] =>
        asgs
          ? asgs.map((asg) => ({
              id: asg.id,
              asgName: asg.asgName,
              optimizationStatus: asg.optimizationStatus.toString(),
              optimisationStatusColor:
                OptimizationStatusColor[
                  Object.keys(OptimizationStatus)[Object.values(OptimizationStatus).indexOf(asg.optimizationStatus)]
                ],
              desiredCapacity: asg.configurations.current.desiredCapacity,
              minCapacity: asg.configurations.current.minCapacity,
              maxCapacity: asg.configurations.current.maxCapacity,
              desiredMinMax: `${asg.configurations.current.desiredCapacity} / ${asg.configurations.current.minCapacity} / ${asg.configurations.current.maxCapacity}`,
              onDemandRatio: asg.configurations.current.onDemandPercentageAboveBaseCapacity,
              spotRatio: 100 - asg.configurations.current.onDemandPercentageAboveBaseCapacity,
              baseCapacity: Math.max(asg.configurations.current.onDemandBaseCapacity, 0),
              onDemandSpotBaseCapacity: `${asg.configurations.current.onDemandPercentageAboveBaseCapacity}% / ${
                100 - asg.configurations.current.onDemandPercentageAboveBaseCapacity
              }% / ${Math.max(asg.configurations.current.onDemandBaseCapacity, 0)}`,
              region: asg.region,
              accountName: asg.accountName,
              accountId: asg.accountId,
              odSpending: verifyNumber(asg.odSpendingByTimeFilter[timeFilter]),
              spotSpending: verifyNumber(asg.spotSpendingByTimeFilter[timeFilter]),
              savings: [OptimizationStatus.Optimized, OptimizationStatus.NewRecommendations].includes(
                asg.optimizationStatus
              )
                ? verifyNumber(asg.costsSavingsHistoryByTimeFilter[timeFilter])
                : getPotentialSavings(asg),
              asgItem: asg,
            }))
          : [],
    [asgs]
  );

  const currentMonthSavings = useMemo(() => {
    const asgTableItems = createAsgTableItems(SavingsTimeFilter.CurrentMonth);
    const optimizedAsgs = asgTableItems.filter(
      (item) =>
        item.optimizationStatus === OptimizationStatus.Optimized ||
        item.optimizationStatus === OptimizationStatus.NewRecommendations
    );

    return calculateSavings(optimizedAsgs, SavingsTimeFilter.CurrentMonth);
  }, [createAsgTableItems]);

  return {
    currentMonthSavings: currentMonthSavings.savings,
    loading: loading || !connectAccounts,
  };
}

/**
 * Hook to get Flexsave savings data for current and last month
 */
export function useFlexsaveSavings() {
  const customerId = useCustomerId();
  const [flexsaveData, loading] = useFlexsaveDoc(customerId);
  const [flexsaveRDSData, loadingRDS] = useFlexsaveRDS(customerId);
  const [flexsaveSageMakerData, loadingSageMaker] = useFlexsaveSageMaker(customerId);

  const currentMonth = getCurrentMonthId();
  const flexsaveComputSavings = useMemo(() => {
    if (!flexsaveData?.AWS?.enabled || !flexsaveData.AWS.savingsSummary || !flexsaveData.AWS.savingsHistory) {
      return {
        currentMonthSavings: 0,
        loading,
      };
    }

    const awsSavings = flexsaveData.AWS.savingsHistory[currentMonth];

    return {
      currentMonthSavings: awsSavings?.savings ?? 0,
      loading,
    };
  }, [flexsaveData, loading, currentMonth]);

  const flexsaveRDSComputSavings = useMemo(() => {
    if (!flexsaveRDSData?.savingsSummary || !flexsaveRDSData.savingsHistory) {
      return {
        currentMonthSavings: 0,
        loading: loadingRDS,
      };
    }

    const rdsSavings = flexsaveRDSData.savingsHistory[currentMonth];

    return {
      currentMonthSavings: rdsSavings?.savings ?? 0,
      loading: loadingRDS,
    };
  }, [flexsaveRDSData, loadingRDS, currentMonth]);

  const flexsaveSageMakerComputSavings = useMemo(() => {
    if (!flexsaveSageMakerData?.savingsSummary || !flexsaveSageMakerData.savingsHistory) {
      return {
        currentMonthSavings: 0,
        loading: loadingSageMaker,
      };
    }

    const sageMakerSavings = flexsaveSageMakerData.savingsHistory[currentMonth];

    return {
      currentMonthSavings: sageMakerSavings?.savings ?? 0,
      loading: loadingSageMaker,
    };
  }, [flexsaveSageMakerData, loadingSageMaker, currentMonth]);

  return {
    loading: loading || loadingRDS || loadingSageMaker,
    currentMonthSavings:
      flexsaveComputSavings.currentMonthSavings +
      flexsaveRDSComputSavings.currentMonthSavings +
      flexsaveSageMakerComputSavings.currentMonthSavings,
  };
}

/**
 * Hook to get aggregated savings data from spot scaling and Flexsave
 * Returns combined savings amount, savings ratio, and loading state
 */
export function useLastMonthSavings() {
  const spotScaling = useSpotScalingSavings();
  const flexsave = useFlexsaveSavings();

  const totalCurrentMonthSavings = spotScaling.currentMonthSavings + flexsave.currentMonthSavings;

  const loading = spotScaling.loading || flexsave.loading;

  return {
    savings: totalCurrentMonthSavings,
    loading,
  };
}
